import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearAuthState, forceSessionRefresh, prepareAuthFlow, generateCodeVerifier } from '../lib/supabase';
import { handleAuthCallback } from '../lib/auth';
import LoadingIndicator from '../components/LoadingIndicator';
import { CheckCircle, AlertCircle, Loader } from 'lucide-react';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState("Processing authentication...");
  const [success, setSuccess] = useState(false);
  const [processingAuth, setProcessingAuth] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(3);

  useEffect(() => {
    const handleRedirect = async () => {
      try {
        setProcessingAuth(true);
        console.log("AuthCallback: Processing callback");
        
        // Clear any existing auth state to prevent conflicts
        clearAuthState();
        
        // Prepare new auth flow
        await prepareAuthFlow();
        
        // Generate a new code verifier for PKCE
        generateCodeVerifier();
        
        // Get the full URL including hash fragments
        const fullUrl = window.location.href;
        console.log("Current URL for processing:", fullUrl);
        
        // Process the authentication callback
        const authResult = await handleAuthCallback(fullUrl);
        
        if (!authResult.success) {
          setError(authResult.error || "Authentication failed");
          return;
        }
        
        setMessage("Authentication successful!");
        setSuccess(true);
        
        // Force a session refresh to ensure the session is properly stored
        await forceSessionRefresh(3);
        
        // Clean up URL by removing the code parameter and hash
        window.history.replaceState({}, '', '/');
        
        // Start countdown for redirect
        startRedirectCountdown();
        
        // Redirect to the appropriate page
        setTimeout(() => {
          if (authResult.redirectTo) {
            window.location.href = authResult.redirectTo;
          } else {
            // Default redirect to home with confirmed flag
            window.location.href = '/?confirmed=true';
          }
        }, redirectCountdown * 1000);
      } catch (err) {
        console.error('AuthCallback: Error handling redirect:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
        setTimeout(() => {
          window.location.href = '/login';
        }, 3000);
      } finally {
        setProcessingAuth(false);
      }
    };
    
    // Start countdown for redirect
    const startRedirectCountdown = () => {
      const interval = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    };
    
    // Check if we have auth parameters in the URL
    if (window.location.search.includes('code=') || 
        window.location.search.includes('token=') || 
        window.location.hash.includes('code=') || 
        window.location.hash.includes('token=')) {
      handleRedirect();
    } else {
      console.log("No auth parameters found in URL");
      setError("No authentication parameters found in URL");
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    }
  }, [navigate, redirectCountdown]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
        <h1 className="text-2xl font-semibold mb-4 text-gray-900">Authentication</h1>
        
        {error ? (
          <>
            <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
              <AlertCircle className="h-5 w-5 mr-2" />
              <span>{error}</span>
            </div>
            <button 
              onClick={() => window.location.href = '/login'}
              className="mt-4 bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition"
            >
              Go to Login
            </button>
          </>
        ) : (
          <>
            {success ? (
              <div className="flex flex-col items-center">
                <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
                <div className="text-green-600 font-medium text-lg mb-4">
                  Successfully logged in!
                </div>
                <div className="text-gray-600">
                  Redirecting to home page in {redirectCountdown} seconds...
                </div>
                <button 
                  onClick={() => window.location.href = '/'}
                  className="mt-4 bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition"
                >
                  Go to Home Now
                </button>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-center mb-4">
                  <Loader className="h-8 w-8 animate-spin text-orange-500" />
                </div>
                <p className="text-gray-700 text-center mb-4">{message}</p>
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 text-blue-700">
                  <p className="text-sm">Please wait while we complete your authentication. This may take a few moments.</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}